import { basePost, baseGet, basePatch, baseDelete, basePut } from "@/utils/api/base";
import apiEndPointList from "@/utils/constant/apiEndPointsList";
import { getSession } from "next-auth/react";
import { isJwtExpired } from "@/app/api/proxy/jwtUtils";

export const getSafeAccessToken = (session) => {
    if (!session || !session.accessToken || session.error === 'RefreshAccessTokenError') return undefined;
    if (isJwtExpired(session.accessToken)) return undefined;
    return session.accessToken;
};

export const getSafeLanguageId = (explicitLang) => {
    const normalize = (l) => {
        if (!l || typeof l !== 'string') return null;
        const clean = l.trim().toLowerCase();
        return (clean === 'vi' || clean === 'vn') ? 'vn' : clean;
    };

    if (explicitLang) {
        const norm = normalize(explicitLang);
        if (norm) return norm;
    }

    if (typeof window === 'undefined') return 'vn';

    try {
        if (window.location && window.location.search) {
            const searchParams = new URLSearchParams(window.location.search);
            const urlLang = searchParams.get('lang');
            const normUrl = normalize(urlLang);
            if (normUrl) return normUrl;
        }

        if (typeof window.getCurrentLanguage === 'function') {
            const lang = window.getCurrentLanguage();
            if (typeof lang === 'string' && lang) {
                const norm = normalize(lang);
                if (norm) return norm;
            }
            if (lang && typeof lang === 'object' && lang.id) {
                const norm = normalize(lang.id);
                if (norm) return norm;
            }
        }

        if (typeof window.id_language === 'string' && window.id_language) {
            const norm = normalize(window.id_language);
            if (norm) return norm;
        }

        if (typeof localStorage !== 'undefined') {
            const stored = localStorage.getItem('language') || localStorage.getItem('language_id') || localStorage.getItem('lang');
            const normStored = normalize(stored);
            if (normStored) return normStored;
        }
    } catch (e) {
        console.warn('Failed to resolve safe language id', e);
    }
    return 'vn';
};

export const resolveIdPath = (id) => {
    if (id) return id;
    if (typeof window === 'undefined') return null;
    return (
        window.id_path ||
        localStorage.getItem('current_project_id') ||
        localStorage.getItem('id_path') ||
        sessionStorage.getItem('project_id_path') ||
        null
    );
};

const functionCallingServices = {
    retrieveKnowledgeBase: async (question) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.RETRIEVE_KNOWLEDGE_BASE,
                headers: {
                    "access-token": accessToken,
                },
                body: { question },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error retrieving knowledge base:", err);
            throw err;
        }
    },

    webSearch: async (query) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.WEB_SEARCH,
                headers: {
                    "access-token": accessToken,
                },
                body: { query },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error web searching:", err);
            throw err;
        }
    },

    retrieveWebContent: async (url) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.RETRIEVE_WEB_CONTENT,
                headers: {
                    "access-token": accessToken,
                },
                body: { url },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error retrieving web content:", err);
            throw err;
        }
    },

    getSectors: async () => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await baseGet({
                endpoint: apiEndPointList.functionCalling.GET_SECTORS,
                headers: {
                    "access-token": accessToken,
                },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error retrieving sectors:", err);
            throw err;
        }
    },

    openSectorList: async () => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.OPEN_SECTOR_LIST,
                headers: {
                    "access-token": accessToken,
                },
                body: {},
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error opening sector list:", err);
            throw err;
        }
    },

    closeSectorList: async () => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.CLOSE_SECTOR_LIST,
                headers: {
                    "access-token": accessToken,
                },
                body: {},
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error closing sector list:", err);
            throw err;
        }
    },

    openSectorProjectList: async () => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.OPEN_SECTOR_PROJECT_LIST,
                headers: { "access-token": accessToken, },
                body: {},
            });
            return response.data;
        } catch (err) {
            console.error("Error opening sector project list:", err);
            throw err;
        }
    },

    closeSectorProjectList: async () => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.CLOSE_SECTOR_PROJECT_LIST,
                headers: { "access-token": accessToken, },
                body: {},
            });
            return response.data;
        } catch (err) {
            console.error("Error closing sector project list:", err);
            throw err;
        }
    },

    disconnectAgent: async () => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.DISCONNECT_AGENT,
                headers: {
                    "access-token": accessToken,
                },
                body: {},
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error disconnecting agent:", err);
            throw err;
        }
    },

    listProjectNames: async () => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await baseGet({
                endpoint: apiEndPointList.functionCalling.LIST_PROJECT_NAMES,
                headers: {
                    "access-token": accessToken,
                },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error listing project names:", err);
            throw err;
        }
    },

    getProjectsByLocation: async (search, field, page, limit) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GET_PROJECTS_BY_LOCATION,
                headers: {
                    "access-token": accessToken,
                },
                body: { search, field, page, limit },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error getting projects by location:", err);
            throw err;
        }
    },

    openProject: async (project_url) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.OPEN_PROJECT,
                headers: {
                    "access-token": accessToken,
                },
                body: { project_url },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error opening project:", err);
            throw err;
        }
    },

    searchProjectId: async (name_path) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.SEARCH_PROJECT_ID,
                headers: {
                    "access-token": accessToken,
                },
                body: { name_path, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error searching project ID:", err);
            throw err;
        }
    },

    getProjectNameById: async (id_path, sublink) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GET_PROJECT_NAME,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, sublink, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error getting project name by ID:", err);
            throw err;
        }
    },

    listSceneNames: async (id_path, name_group, page, offset, sublink, language_id) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            const effectiveLang = getSafeLanguageId(language_id);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.LIST_SCENE_NAMES,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, name_group, page, offset, sublink, language_id: effectiveLang },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error listing scene names:", err);
            throw err;
        }
    },

    searchSceneName: async (id_path, searchTerm) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            let lang = getSafeLanguageId();
            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.SEARCH_SCENE_NAME,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, searchTerm, language_id: lang },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error searching scene name:", err);
            throw err;
        }
    },

    getSceneGroupBySceneName: async (id_path, scene_name) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GET_SCENE_GROUP,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, scene_name, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error getting scene group by scene name:", err);
            throw err;
        }
    },

    getGroupInfo: async (id_path, group_scene) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GET_GROUP_INFO,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, group_scene, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error getting group info:", err);
            throw err;
        }
    },

    getAllModelNames: async (id_path, sublink) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GET_ALL_MODEL_NAMES,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, language_id: getSafeLanguageId(), sublink },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error getting all model names:", err);
            throw err;
        }
    },

    getAllBookNames: async (id_path) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GET_ALL_BOOK_NAMES,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error getting all book names:", err);
            throw err;
        }
    },

    getAllPhotoNames: async (id_path) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GET_ALL_PHOTO_NAMES,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error getting all photo names:", err);
            throw err;
        }
    },

    getAllVideoNames: async (id_path) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GET_ALL_VIDEO_NAMES,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error getting all video names:", err);
            throw err;
        }
    },

    searchModelIdByName: async (id_path, searchTerm, language_id) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            const currentLang = typeof window !== 'undefined' && typeof window.getCurrentLanguage === 'function'
                ? window.getCurrentLanguage()?.id
                : undefined;
            const resolvedLang = language_id || currentLang || getSafeLanguageId();

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.SEARCH_MODEL_ID,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, searchTerm, language_id: resolvedLang },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error searching model ID by name:", err);
            throw err;
        }
    },

    searchBookIdByName: async (id_path, searchTerm) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.SEARCH_BOOK_ID,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, searchTerm, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error searching book ID by name:", err);
            throw err;
        }
    },

    searchPhotoIdByName: async (id_path, searchTerm) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.SEARCH_PHOTO_ID,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, searchTerm, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error searching photo ID by name:", err);
            throw err;
        }
    },

    searchVideoIdByName: async (id_path, searchTerm) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.SEARCH_VIDEO_ID,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, searchTerm, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error searching video ID by name:", err);
            throw err;
        }
    },

    searchGroupInfo: async (id_path, searchTerm) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.SEARCH_GROUP_INFO,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, searchTerm, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error searching group info:", err);
            throw err;
        }
    },

    getModelsInCurrentScene: async (id_path, roomId) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GET_MODELS_IN_CURRENT_SCENE,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, roomId, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error getting models in current scene:", err);
            throw err;
        }
    },

    getScenesInGroupScene: async (id_path, searchTerm) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GET_SCENES_IN_GROUP_SCENE,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, searchTerm, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error getting scenes in group:", err);
            throw err;
        }
    },

    getAssetInfo: async (id_path, asset_id) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GET_ASSET_INFO,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, asset_id, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error getting asset info:", err);
            throw err;
        }
    },

    goToScene: async (id_path, sceneName) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GO_TO_SCENE,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, sceneName, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error going to scene:", err);
            throw err;
        }
    },

    goToHotSpot: async (id_path, hotspotName) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GO_TO_HOTSPOT,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, hotspotName, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error going to hotspot:", err);
            throw err;
        }
    },

    navigateToScene: async (id_path, scene_query, roomId, language, sublink = null) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            let lang = language || getSafeLanguageId();
            let effectiveIdPath = resolveIdPath(id_path);
            let current_scene = null;
            try {
                if (typeof window !== 'undefined') {
                    current_scene = window.currentSceneName || (window.krpano && window.krpano.get("xml.scene")) || null;
                }
            } catch (e) {
                console.warn('Could not get current scene:', e);
            }
            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.NAVIGATE_TO_SCENE,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path: effectiveIdPath, scene_query, roomId, language_id: lang, current_scene, sublink },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error scene navigation:", err);
            throw err;
        }
    },

    openModel: async (id_path, model_query, language_id) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            const currentLang = typeof window !== 'undefined' && typeof window.getCurrentLanguage === 'function'
                ? window.getCurrentLanguage()?.id
                : undefined;
            const resolvedLang = language_id || currentLang || getSafeLanguageId();

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.OPEN_MODEL,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, model_query, language_id: resolvedLang },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error viewing to model:", err);
            throw err;
        }
    },

    openBook: async (id_path, book_query, roomId) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.OPEN_BOOK,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, book_query, roomId, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error opening to book:", err);
            throw err;
        }
    },

    openPhoto: async (id_path, photo_query, roomId) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.OPEN_PHOTO,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, photo_query, roomId, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error displaying to photo:", err);
            throw err;
        }
    },

    openVideo: async (id_path, video_query) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);
            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.OPEN_VIDEO,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, video_query, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error playing to video:", err);
            throw err;
        }
    },

    getCurrentSceneInfo: async (id_path, roomId, language, sublink = null) => {
        try {

            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            // Lấy thông tin scene hiện tại từ krpano nếu có
            let currentSceneName = null;
            try {
                if (typeof window !== 'undefined') {
                    currentSceneName = window.currentSceneName || (window.krpano && window.krpano.get("xml.scene")) || null;
                }
            } catch (e) {
                console.warn('Could not get current scene:', e);
            }

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GET_CURRENT_SCENE_INFO,
                headers: {
                    "access-token": accessToken,
                },
                body: {
                    id_path: id_path,
                    roomId: roomId,
                    currentSceneName: currentSceneName, // Gửi thông tin scene hiện tại
                    language_id: language,
                    sublink: sublink,
                },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error getting current scene info:", err);
            throw err;
        }
    },

    createVirtualTour: async (id_path, tour_name, scene_queries, scenes_per_location, roomId, narration, sublink = null) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.CREATE_VIRTUAL_TOUR,
                headers: {
                    "access-token": accessToken,
                },
                body: { id_path, tour_name, scene_queries, scenes_per_location, roomId, narration, language_id: getSafeLanguageId(), sublink },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error creating virtual tour:", err);
            throw err;
        }
    },

    saveVirtualTour: async (id_path, tour_name, roomId) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.SAVE_VIRTUAL_TOUR(id_path),
                headers: {
                    "access-token": accessToken,
                },
                body: { tour_name, roomId, language_id: getSafeLanguageId() },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error saving virtual tour:", err);
            throw err;
        }
    },

    getWebcamDescription: async (query, imageBlob) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const formData = new FormData();
            formData.append('query', query);
            formData.append('image', imageBlob, 'webcam.jpg');

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.GET_WEBCAM_DESCRIPTION,
                headers: {
                    "access-token": accessToken,
                },
                body: formData,
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error getting webcam description:", err);
            throw err;
        }
    },

    /**
     * Retrieve knowledge for a specific object/hotspot visible in the 360° tour.
     * Used by the Gemini Live tool `retrieve_object_knowledge` to get
     * hotspot metadata + RAG-retrieved technical documentation.
     *
     * @param {string} idPath - Project id_path for DB routing
     * @param {string} objectId - Hotspot name/ID from spatial context (e.g. "hs_123")
     * @param {string} query - User's question about the object
     * @param {string|null} sceneId - Optional current scene ID
     * @returns {Promise<{success: boolean, data: {object_id, hotspot, knowledge, source}}>}
     */
    retrieveObjectKnowledge: async (idPath, objectId, query, sceneId = null) => {
        try {
            const session = await getSession();
            const accessToken = getSafeAccessToken(session);

            const response = await basePost({
                endpoint: apiEndPointList.functionCalling.RETRIEVE_OBJECT_KNOWLEDGE,
                headers: {
                    "access-token": accessToken,
                },
                body: {
                    id_path: idPath,
                    object_id: objectId,
                    query,
                    scene_id: sceneId,
                },
            });
            const { data } = response;
            return data;
        } catch (err) {
            console.error("Error retrieving object knowledge:", err);
            throw err;
        }
    },
};

export default functionCallingServices;
